import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { monetaryFormat } from '@/utils/monetary-format';
import type { MappedFeature } from '../utils/map-graphql-to-calculator.util';

interface UploadSizeAddOnRowProps {
    /** upload-size-images feature — drives the selection. */
    imageFeature: MappedFeature;
    /** upload-size-videos feature — mirrored by option order, priced at 0€. */
    videoFeature: MappedFeature;
    /** Currently selected image option code (or the plan's included image code). */
    selectedValue: string;
    currency?: { symbol: string } | null;
    /** Fires with the image feature code; the state hook mirrors the video tier. */
    onChange: (code: string, value: string) => void;
    disabled?: boolean;
}

/**
 * Image + video upload size upgrade together as a single bundle, so they're presented
 * as one control: picking an image tier also unlocks the paired video tier (5 MB / 100 MB,
 * 25 MB / 500 MB, 50 MB / 2 GB). Only the image tier carries a price; video is included.
 */
export const UploadSizeAddOnRow = ({
    imageFeature,
    videoFeature,
    selectedValue,
    currency,
    onChange,
    disabled,
}: UploadSizeAddOnRowProps) => {
    const { t } = useTranslation();

    const imageOptions = imageFeature.addOnOptions ?? [];
    const videoOptions = videoFeature.addOnOptions ?? [];

    const hasIncluded = imageFeature.displayValue !== '';
    const baseIndex = hasIncluded ? imageOptions.findIndex((o) => o.value === imageFeature.displayValue) : -1;

    const currentIndex = imageOptions.findIndex((o) => o.value === selectedValue);
    const effectiveValue = currentIndex !== -1 ? selectedValue : '';
    const nothingSelected = currentIndex === -1;
    const price = imageOptions[currentIndex]?.price ?? 0;

    /** "25 MB images · 500 MB video" — the two tiers paired by option order. */
    const pairLabel = (idx: number) =>
        t('billing.calculator.upload-size.pair', {
            image: t(imageOptions[idx]?.label ?? ''),
            video: t(videoOptions[idx]?.label ?? ''),
        });

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', md: '1fr 220px 140px' },
                gridTemplateRows: { xs: 'auto auto', md: '1fr' },
                alignItems: 'center',
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
                gap: { xs: 1, md: 2 },
            }}
        >
            <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Typography variant="body2" fontWeight={600}>
                    {t('billing.calculator.upload-size.name')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    {t('billing.calculator.upload-size.description')}
                </Typography>
                <Typography variant="caption" color={hasIncluded ? 'success.main' : 'text.secondary'}>
                    {hasIncluded
                        ? t('billing.calculator.addon.included-value', { value: pairLabel(baseIndex) })
                        : t('billing.calculator.addon.not-included-in-plan')}
                </Typography>
            </Box>

            <Select
                size="small"
                value={effectiveValue}
                displayEmpty
                disabled={!!disabled}
                onChange={(e) => onChange(imageFeature.code, e.target.value)}
                sx={{ fontSize: '0.875rem', width: '100%' }}
                renderValue={
                    nothingSelected
                        ? () => <em style={{ opacity: 0.5 }}>{t('billing.calculator.addon.select-option')}</em>
                        : () => pairLabel(currentIndex)
                }
            >
                {imageOptions.map((opt, idx) => {
                    const isDowngrade = baseIndex !== -1 && idx < baseIndex;
                    return (
                        <MenuItem key={opt.value} value={opt.value} disabled={isDowngrade}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                                spacing={2}
                            >
                                <span>{pairLabel(idx)}</span>
                                {!isDowngrade && opt.value !== imageFeature.displayValue && (
                                    <Typography variant="caption" color="text.secondary">
                                        {monetaryFormat(opt.price, currency)}
                                        {t('billing.calculator.per-month')}
                                    </Typography>
                                )}
                            </Stack>
                        </MenuItem>
                    );
                })}
            </Select>

            <Box sx={{ textAlign: 'right' }}>
                {nothingSelected ? (
                    <Typography variant="body2" color="text.disabled">
                        —
                    </Typography>
                ) : price > 0 && effectiveValue !== imageFeature.displayValue ? (
                    <>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {t('billing.calculator.addon.upgrade-cost')}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                            +{monetaryFormat(price, currency)}
                            {t('billing.calculator.per-month')}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body2" color="text.disabled">
                        {t('billing.calculator.addon.included')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
